import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Loader2, ArrowLeft, Save, DollarSign, UserPlus, HeartHandshake, Lock, Clock, FileSignature, Video,
} from "lucide-react";
import { formatCurrency } from "@/lib/format";
import { pretty, LANES, OPP_STAGE_HEX, REVENUE_HEX, hexWithAlpha, GRAY, AMBER } from "./crmConstants";
import RevenueOutcomeDialog from "./RevenueOutcomeDialog";

const Row = ({ label, children }) => (
  <div><label className="text-[11px] uppercase tracking-wide text-[#4B5563]">{label}</label>{children}</div>
);

export default function CrmOpportunityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [revOpen, setRevOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/crm/opportunities/${id}`);
      setD(data);
      const o = data.opportunity;
      setForm({
        name: o.name || "", service_lane: o.service_lane || "sell_home", stage: o.stage || "opportunity_identified",
        revenue_status: o.revenue_status || "not_started", estimated_value: o.estimated_value ?? "",
        actual_value: o.actual_value ?? "", expected_close_date: (o.expected_close_date || "").slice(0, 10),
        notes: o.notes || "", opened_by_user_id: o.opened_by_user_id || "", managed_by_user_id: o.managed_by_user_id || "",
        closed_by_user_id: o.closed_by_user_id || "", agreement_needed: !!o.agreement_needed,
        agreement_type: o.agreement_type || "", professional_involved: !!o.professional_involved,
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load."); navigate("/crm/opportunities"); }
    finally { setLoading(false); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        estimated_value: form.estimated_value === "" ? null : Number(form.estimated_value),
        actual_value: form.actual_value === "" ? null : Number(form.actual_value),
        expected_close_date: form.expected_close_date || null,
        opened_by_user_id: form.opened_by_user_id || null,
        managed_by_user_id: form.managed_by_user_id || null,
        closed_by_user_id: form.closed_by_user_id || null,
      };
      await api.put(`/crm/opportunities/${id}`, payload);
      toast.success("Saved.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
    finally { setSaving(false); }
  };

  if (loading || !form) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div>;
  const o = d.opportunity;
  const staff = d.staff || [];
  const stages = d.stages || [];
  const revStatuses = d.revenue_statuses || [];
  const stageHex = OPP_STAGE_HEX[form.stage] || GRAY;
  const revHex = REVENUE_HEX[form.revenue_status] || GRAY;

  const staffSel = (value, onChange, testid) => (
    <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
      <SelectTrigger data-testid={testid} className="h-9 text-[13px]"><SelectValue placeholder="—" /></SelectTrigger>
      <SelectContent><SelectItem value="none">—</SelectItem>{staff.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
    </Select>
  );

  return (
    <div className="max-w-5xl mx-auto pb-16" data-testid="crm-opportunity-detail">
      <button onClick={() => navigate("/crm/opportunities")} className="flex items-center gap-1.5 text-[14px] text-[#1C4358] hover:underline mb-3"><ArrowLeft size={15} /> Back to opportunities</button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-bold text-[#1C4358]">{o.name}</h1>
          <div className="text-[13px] text-[#4B5563] mt-1">{pretty(o.service_lane)}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[12px] font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: hexWithAlpha(revHex, 0.14), color: revHex }}>{pretty(o.revenue_status)}</span>
          <span className="text-[12px] font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: hexWithAlpha(stageHex, 0.14), color: stageHex }}>{pretty(o.stage)}</span>
          <Button data-testid="opp-revenue-outcome-btn" onClick={() => setRevOpen(true)} className="h-9 bg-[#2F7D5A] hover:bg-[#276848] text-white gap-1"><DollarSign size={14} /> Add Revenue Outcome</Button>
          <Button data-testid="opp-schedule-meeting-btn" onClick={() => navigate(`/meetings?new=1&opportunity=${id}${o.lead_id ? `&lead=${o.lead_id}` : ""}`)} className="h-9 bg-[#1C4358] hover:bg-[#163545] text-white gap-1"><Video size={14} /> Schedule meeting</Button>
        </div>
      </div>

      <RevenueOutcomeDialog open={revOpen} onOpenChange={setRevOpen} opportunityId={id} leadId={o.lead_id || null}
        contextLabel={o.name}
        defaults={{ service_lane: o.service_lane, relationship_id: o.relationship_id, estimated_value: o.estimated_value,
          actual_value: o.actual_value, revenue_status: o.revenue_status, opened_by_user_id: o.opened_by_user_id,
          managed_by_user_id: o.managed_by_user_id, closed_by_user_id: o.closed_by_user_id }}
        onSaved={load} />

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        {/* Edit form */}
        <Card className="p-4 border-[#E6EDF2] lg:col-span-2" data-testid="opp-edit-card">
          <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-3">Deal details</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Row label="Name"><Input data-testid="opp-edit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-9 text-[14px]" /></Row></div>
            <Row label="Service lane">
              <Select value={form.service_lane} onValueChange={(v) => setForm({ ...form, service_lane: v })}><SelectTrigger className="h-9 text-[13px]"><SelectValue /></SelectTrigger><SelectContent>{LANES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent></Select>
            </Row>
            <Row label="Stage">
              <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}><SelectTrigger data-testid="opp-edit-stage" className="h-9 text-[13px]"><SelectValue /></SelectTrigger><SelectContent>{stages.map((s) => <SelectItem key={s} value={s}>{pretty(s)}</SelectItem>)}</SelectContent></Select>
            </Row>
            <Row label="Expected close date"><Input type="date" value={form.expected_close_date} onChange={(e) => setForm({ ...form, expected_close_date: e.target.value })} className="h-9 text-[13px]" /></Row>
            <Row label="Agreement type"><Input value={form.agreement_type} onChange={(e) => setForm({ ...form, agreement_type: e.target.value })} placeholder="e.g. listing agreement" className="h-9 text-[14px]" /></Row>
            <Row label="Opened by">{staffSel(form.opened_by_user_id, (v) => setForm({ ...form, opened_by_user_id: v }), "opp-opened-by")}</Row>
            <Row label="Managed by">{staffSel(form.managed_by_user_id, (v) => setForm({ ...form, managed_by_user_id: v }), "opp-managed-by")}</Row>
            <Row label="Closed by">{staffSel(form.closed_by_user_id, (v) => setForm({ ...form, closed_by_user_id: v }), "opp-closed-by")}</Row>
            <div className="flex items-center gap-4 pt-5">
              <label className="flex items-center gap-1.5 text-[13px] text-[#4B5563]"><input type="checkbox" checked={form.agreement_needed} onChange={(e) => setForm({ ...form, agreement_needed: e.target.checked })} /> Agreement needed</label>
              <label className="flex items-center gap-1.5 text-[13px] text-[#4B5563]"><input type="checkbox" checked={form.professional_involved} onChange={(e) => setForm({ ...form, professional_involved: e.target.checked })} /> Professional involved</label>
            </div>
          </div>
          <div className="mt-3">
            <label className="text-[11px] uppercase tracking-wide text-[#4B5563]">Notes</label>
            <Textarea data-testid="opp-edit-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="text-[14px]" />
          </div>
          <Button data-testid="opp-save-btn" onClick={save} disabled={saving} className="mt-3 bg-[#1C4358] hover:bg-[#152F3F] text-white gap-1">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save changes</Button>
        </Card>

        {/* Revenue placeholders (internal only) */}
        <Card className="p-4 border-[#E6EDF2]" data-testid="opp-revenue-card">
          <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2 flex items-center gap-1.5"><DollarSign size={13} /> Revenue (internal)</div>
          <Row label="Estimated value ($)"><Input data-testid="opp-est-value" type="number" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} className="h-9 text-[14px]" /></Row>
          <div className="mt-2"><Row label="Actual value ($)"><Input data-testid="opp-actual-value" type="number" value={form.actual_value} onChange={(e) => setForm({ ...form, actual_value: e.target.value })} className="h-9 text-[14px]" /></Row></div>
          <div className="mt-2"><Row label="Revenue status">
            <Select value={form.revenue_status} onValueChange={(v) => setForm({ ...form, revenue_status: v })}><SelectTrigger data-testid="opp-revenue-status" className="h-9 text-[13px]"><SelectValue /></SelectTrigger><SelectContent>{revStatuses.map((s) => <SelectItem key={s} value={s}>{pretty(s)}</SelectItem>)}</SelectContent></Select>
          </Row></div>
          <div className="mt-3 flex items-start gap-2 rounded-lg px-3 py-2 text-[12px]" style={{ backgroundColor: hexWithAlpha(AMBER, 0.1), color: "#8A5A12" }}>
            <Lock size={13} className="mt-0.5 shrink-0" /> Internal-only placeholder. Compensation / money-flow formulas are configurable later.
          </div>
        </Card>
      </div>

      {/* Links: lead + relationship + follow-ups */}
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <Card className="p-4 border-[#E6EDF2]" data-testid="opp-links">
          <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">Linked records</div>
          {d.linked_lead ? (
            <div data-testid="opp-linked-lead" onClick={() => navigate(`/crm/leads/${d.linked_lead.id}`)} className="flex items-center justify-between gap-2 py-2 cursor-pointer hover:bg-[#F5F8FA] px-1 rounded">
              <span className="flex items-center gap-1.5 text-[14px] text-[#252221]"><UserPlus size={13} className="text-[#2C6480]" /> {d.linked_lead.name}</span>
              <span className="text-[11px] text-[#4B5563]">{pretty(d.linked_lead.pipeline_stage)}</span>
            </div>
          ) : <p className="text-[13px] text-[#4B5563] py-1">No lead linked.</p>}
          {d.linked_relationship ? (
            <div data-testid="opp-linked-rel" onClick={() => navigate(`/crm/relationships/${d.linked_relationship.id}`)} className="flex items-center justify-between gap-2 py-2 cursor-pointer hover:bg-[#F5F8FA] px-1 rounded border-t border-[#F0F4F7]">
              <span className="flex items-center gap-1.5 text-[14px] text-[#252221]"><HeartHandshake size={13} className="text-[#9C1E22]" /> {d.linked_relationship.name}</span>
              <span className="text-[11px] text-[#4B5563]">{pretty(d.linked_relationship.relationship_type)}</span>
            </div>
          ) : <p className="text-[13px] text-[#4B5563] py-1 border-t border-[#F0F4F7]">No partner/vendor linked.</p>}
        </Card>

        <Card className="p-4 border-[#E6EDF2]" data-testid="opp-followups">
          <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2 flex items-center gap-1.5"><Clock size={13} /> Follow-ups (on linked lead)</div>
          {(d.follow_ups || []).length === 0 ? <p className="text-[13px] text-[#4B5563]">None scheduled.</p> : (
            <div className="space-y-1.5">
              {d.follow_ups.map((f) => (
                <div key={f.id} className="text-[12px] text-[#4B5563] flex items-center gap-1.5"><FileSignature size={11} /> {f.due_date} — {f.note || pretty(f.follow_up_type)} <span className="text-[#9AA5B1]">· {pretty(f.status)}</span></div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
